import type { ConversationAttributeItem } from '@/entities/attributes/model/api/types';
import { ATTRIBUTE_TYPES, getAttributeValueFilter } from '@/entities/attributes';
import type { TimeIntervalFormat } from '@/shared/lib/utils/string';
import { type AttributeFilter, type AttributeFilterKind, dataGridValueToAttributeFilter } from '@/entities/dashboards';

/**
 * Доступен ли атрибут для добавления в панель фильтров.
 * Структурный атрибут значения не хранит — отбирать по нему нечего.
 */
export const isFilterableAttribute = (attribute: ConversationAttributeItem): boolean =>
  !attribute.isDeleted && attribute.dataType !== ATTRIBUTE_TYPES.STRUCTURE;

/**
 * Вид условия по значению атрибута либо `null`, если по атрибуту не отбирают.
 * Само правило вида — общее с колонками грида диалогов ({@link getAttributeValueFilter}):
 * здесь только отсекаются атрибуты, по которым отбора нет.
 */
export const getAttributeFilterKind = (
  attribute: ConversationAttributeItem | undefined,
  timeDisplayFormat: TimeIntervalFormat,
): AttributeFilterKind | null => {
  if (!attribute || !isFilterableAttribute(attribute)) return null;
  return getAttributeValueFilter(attribute, timeDisplayFormat).type;
};

/**
 * Создаёт пустую структуру фильтра под выбранный атрибут.
 * Форма результата — та же, что у сброшенного отбора того же вида, поэтому она собирается
 * тем же адаптером, что и значение из поля ввода.
 * Возвращает `null`, если по атрибуту не отбирают (структурный либо удалённый).
 */
export const createEmptyFilter = (
  attribute: ConversationAttributeItem,
  timeDisplayFormat: TimeIntervalFormat,
): AttributeFilter | null => {
  const kind = getAttributeFilterKind(attribute, timeDisplayFormat);
  if (kind === null) return null;
  return dataGridValueToAttributeFilter(attribute.path, kind, undefined);
};

/**
 * Миграция формата атрибутного фильтра при чтении из uiSettings.
 * До «Доработок СКТ — 3» элемент массива хранил ключ `code`; новый контракт требует `path`.
 * Сохранённые ранее значения переименовываются на лету при загрузке настроек.
 * TODO: удалить после следующего релиза после массовой миграции uiSettings пользователей.
 */
export const migrateLegacyAttributeFilters = (filters: AttributeFilter[] | undefined): AttributeFilter[] => {
  if (!filters) return [];
  return filters.map((filter) => {
    if ('path' in filter && filter.path) return filter;
    const legacy = filter as unknown as { code?: string };
    if (typeof legacy.code === 'string') {
      const { code: _legacyCode, ...rest } = legacy as { code: string } & Record<string, unknown>;
      return { ...(rest as object), path: legacy.code } as AttributeFilter;
    }
    return filter;
  });
};
